import { useEffect, useState, type CSSProperties } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "./api.ts";

/* ──────────────────────────────────────────────────────────────────────────
   A creator's photo, wherever a creator's name is shown (Jay, 2026-10-01).

   Every photo comes from one store (modules/creators/server/lib/photos.ts).
   Each module serves it through its own two routes under its own base,
   `${base}/creator-photos` (name to version) and `${base}/creator-photo/:name`,
   so the permission that shows a page also shows the faces on it.

   Styled inline, not with the kit's .avatar class, because some pages draw
   inside a shadow root (Reporting) or under a lifted stylesheet (Uploader)
   where the kit's classes do not reach. With no photo it is the initials.
   ────────────────────────────────────────────────────────────────────────── */

export type CreatorPhotos = (name: string | null | undefined) => string | null;

export function useCreatorPhotos(base: string): CreatorPhotos {
  const index = useQuery({
    queryKey: ["creator-photos", base],
    queryFn: () => api<{ photos: Record<string, string> }>(`${base}/creator-photos`),
    staleTime: 60_000,
    retry: false,
  });
  const photos = index.data?.photos ?? {};
  return (name) => {
    const key = (name ?? "").trim().toUpperCase();
    const version = key ? photos[key] : undefined;
    return version ? `${base}/creator-photo/${encodeURIComponent(key)}?v=${encodeURIComponent(version)}` : null;
  };
}

const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? "")
    .join("")
    .toUpperCase() || "?";

export function CreatorAvatar({ name, src, size = 22, style, title }: { name: string; src: string | null; size?: number; style?: CSSProperties; title?: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  const box: CSSProperties = {
    width: size,
    height: size,
    flex: "none",
    borderRadius: "50%",
    overflow: "hidden",
    display: "inline-grid",
    placeItems: "center",
    background: "var(--panel-2, #1b1f23)",
    border: "1px solid var(--line-soft, rgba(255,255,255,.08))",
    color: "var(--muted, #9aa1a8)",
    fontSize: Math.max(7, Math.round(size * 0.36)),
    fontWeight: 650,
    lineHeight: 1,
    verticalAlign: "middle",
    ...style,
  };
  return (
    <span style={box} title={title ?? name} aria-hidden="true">
      {src && !failed ? (
        <img src={src} alt="" loading="lazy" decoding="async" onError={() => setFailed(true)} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      ) : (
        initials(name)
      )}
    </span>
  );
}
